import { useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import type { VideoItem } from "@/types/video";
import {
  formatDate,
  formatDuration,
  formatViews,
  getCategoryName,
  getVideo,
} from "@/lib/videos";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function FavoritesPage() {
  const { favorites, remove, clear, count } = useFavorites();
  const [confirmOpen, setConfirmOpen] = useState(false);

  usePageMeta({
    title: "我的收藏 - 茄子视频",
    description:
      "茄子视频收藏列表，收藏记录保存在浏览器本地，可随时移除单条视频或清空全部收藏，无需登录。",
    keywords: "茄子视频收藏,我的收藏,短视频收藏,本地收藏",
  });

  const items = favorites
    .map((id) => getVideo(id))
    .filter((item): item is VideoItem => Boolean(item));

  const handleClear = () => {
    clear();
    setConfirmOpen(false);
  };

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-[1200px] px-4 pt-6 md:px-8 md:pt-8">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "我的收藏" }]} />

        <div className="mt-5 flex flex-wrap items-end justify-between gap-5 rounded-2xl bg-primary-50 px-5 py-7 md:px-8 md:py-9">
          <div>
            <p className="flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-primary-600">
              <span className="h-1 w-6 rounded-full bg-accent-500"></span>
              My collection
            </p>
            <h1 className="mt-2 font-heading text-2xl font-extrabold tracking-tight text-foreground-950 md:text-[32px]">
              我的收藏
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              收藏记录保存在当前浏览器本地，不需要登录。换设备或清理浏览器数据后会消失。
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-background-50 px-3 py-1.5 font-label text-xs text-foreground-600">
              共 {count} 条
            </span>
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              disabled={count === 0}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <i className="ri-delete-bin-6-line text-sm"></i>
              清空收藏
            </button>
          </div>
        </div>

        <div className="mt-8 pb-4">
          {items.length === 0 ? (
            <EmptyState
              icon="ri-heart-3-line"
              title="收藏夹还是空的"
              description="在视频详情页点击“收藏这条视频”，它就会出现在这里。先去首页逛逛，遇到喜欢的再收起来。"
            >
              <Link
                to="/"
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                去首页逛逛
              </Link>
              <Link
                to="/category/entertainment"
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                按分类浏览
              </Link>
            </EmptyState>
          ) : (
            <div className="flex flex-col gap-3">
              {items.map((video) => (
                <div
                  key={video.id}
                  className="flex flex-col gap-4 rounded-xl border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200 sm:flex-row sm:items-center sm:p-4"
                >
                  <Link
                    to={`/video/${video.id}`}
                    className="group relative aspect-video w-full shrink-0 overflow-hidden rounded-lg bg-background-200 sm:w-[220px]"
                  >
                    <img
                      src={video.cover}
                      alt={video.title}
                      title={`${video.title} - 茄子视频收藏`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute bottom-2 right-2 rounded bg-foreground-950/75 px-1.5 py-0.5 font-label text-[11px] text-background-50">
                      {formatDuration(video.duration)}
                    </span>
                  </Link>

                  <div className="min-w-0 flex-1">
                    <Link
                      to={`/video/${video.id}`}
                      className="line-clamp-2 text-[15px] font-medium leading-snug text-foreground-900 transition-colors hover:text-primary-600"
                    >
                      {video.title}
                    </Link>
                    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-500">
                      <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] text-secondary-900">
                        {getCategoryName(video.categoryId)}
                      </span>
                      <span>{formatViews(video.views)} 播放</span>
                      <span className="text-foreground-300">·</span>
                      <span>{formatDate(video.uploadTime)}</span>
                      <span className="hidden text-foreground-300 sm:inline">·</span>
                      <span className="hidden sm:inline">{video.author}</span>
                    </div>
                    <p className="mt-2 hidden line-clamp-1 text-xs leading-relaxed text-foreground-500 md:block">
                      {video.description}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 sm:shrink-0">
                    <Link
                      to={`/video/${video.id}`}
                      className="flex flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600 sm:flex-none"
                    >
                      <i className="ri-play-fill text-sm"></i>
                      播放
                    </Link>
                    <button
                      type="button"
                      onClick={() => remove(video.id)}
                      aria-label={`移除收藏：${video.title}`}
                      className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-500 transition-colors hover:border-primary-300 hover:text-primary-600 sm:h-9 sm:w-9"
                    >
                      <i className="ri-close-line text-base"></i>
                    </button>
                  </div>
                </div>
              ))}

              <div className="mt-2 flex justify-center">
                <Link
                  to="/"
                  className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className="ri-arrow-left-line"></i>
                  继续发现更多视频
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>

      {confirmOpen && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <button
            type="button"
            aria-label="关闭确认弹窗"
            onClick={() => setConfirmOpen(false)}
            className="absolute inset-0 cursor-pointer bg-foreground-950/45"
          ></button>
          <div className="relative w-full max-w-sm rounded-xl bg-background-50 p-6">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-100 text-accent-700">
              <i className="ri-delete-bin-6-line text-xl"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">
              清空全部收藏？
            </h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-500">
              将移除 {count} 条收藏记录，此操作无法撤销。视频本身不会删除，你仍可以在分类页找到它们。
            </p>
            <div className="mt-6 flex items-center gap-3">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-background-300 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100"
              >
                取消
              </button>
              <button
                type="button"
                onClick={handleClear}
                className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-600 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                确认清空
              </button>
            </div>
          </div>
        </div>
      )}
    </SiteLayout>
  );
}